/* eslint-disable import/no-cycle */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import AppStack from '../../../../common/AppStack';
import AppText from '../../../../common/AppText';
import { Context as StoreUserContext } from '../../../../../providers/StoreUserContextProvider';
import WsSectionContainer from '../WsSectionContainer';
import {
  IMAGE_DISPLAY_ENUM,
  QUICK_ADD_STYLE_ENUM
} from './wsFeaturedCollectionConfig';
import WsProductCard from '../productGrid/WsProductCard';
import { sterilizeUrlHandle } from '../../../../../helpers/format';
import WsSectionViewAllButtons from '../WsSectionViewAllButtons';
import {
  getSelectedResourceBaseNavigationUrl,
  getWebstoreHeadingSize
} from '../../../../../helpers/webstoreHelper';

const WsFeaturedCollection = ({
  config,
  colorSchemes,
  themeConfig,
  baseStoreUrl,
  onNavigate,
  ecomStoreTheme,
  mediaQueries,
  resourceNavigation,
  ...rest
}) => {
  const { state } = useContext(StoreUserContext);
  const { isTabletOrSmaller } = mediaQueries;

  const ecomStoreCollection = state.ecomStoreCollectionHistory.value.find(
    (c) =>
      c.pkEcomStoreCollection.toString() ===
      config.ecomStoreCollection?.toString()
  );
  const vendorProducts = config.ecomStoreCollection
    ? ecomStoreCollection?.ecomStoreCollectionProducts.map(
        (p) => p.ecomStoreProduct
      ) ?? []
    : state.paginatedEcomStoreProductsPreview.data;

  const loading = config.ecomStoreCollection
    ? !ecomStoreCollection || state.ecomStoreCollectionHistory.loading
    : !state.paginatedEcomStoreProductsPreview.initialized ||
      state.paginatedEcomStoreProductsPreview.loading;

  const colorScheme = colorSchemes[config.colorScheme - 1] ?? colorSchemes[0];
  const showSecondImageOnHover = config.showSecondImageOnHover === 'true';
  const showVendorName = config.showVendorName === 'true';
  const showVariantsSeparately = config.showVariantsSeparately === 'true';
  const variantSelector = config.seperateVariantsSelector;
  const maxDisplayed = config.maximumProductsDisplayed * 1 ?? 25;
  const squareRatio = config.imageRatio === IMAGE_DISPLAY_ENUM.SQUARE;
  const isQuickAddEnabled =
    config.quickAddStyle === QUICK_ADD_STYLE_ENUM.STANDARD;

  const collectionNotFound = state.ecomStoreCollections.idsNotFound.includes(
    config.ecomStoreCollection?.toString()
  );

  const hideAllProductBadges = themeConfig.hideAllProductBadges === 'true';
  const hideFreeShippingBadge = themeConfig.hideFreeShippingBadge === 'true';
  const baseProductsResourceUrl = `${baseStoreUrl}${getSelectedResourceBaseNavigationUrl(
    resourceNavigation,
    'products'
  )}`;

  return (
    <WsSectionContainer
      colorScheme={colorScheme}
      config={config}
      mediaQueries={mediaQueries}
      themeConfig={themeConfig}
      withPadding
      {...rest}
    >
      <AppStack
        style={{
          flex: 1,
          justifyContent: 'space-between',
          margin: 'auto',
          width: '100%',
          gap: 8
        }}
      >
        {config.headingText && (
          <AppText
            style={{
              fontWeight: 600,
              fontSize: getWebstoreHeadingSize(config.headingSize)
            }}
          >
            {config.headingText}
          </AppText>
        )}
        {config.descriptionText && (
          <AppText style={{ fontSize: 16 }}>
            {config.showCollectionDescription === 'true'
              ? ecomStoreCollection?.description
              : config.descriptionText}
          </AppText>
        )}
        <AppStack
          style={{
            flex: 1,
            justifyContent: 'space-between',
            margin: 'auto',
            width: '100%',
            gap: 0
          }}
        >
          {collectionNotFound || (!loading && vendorProducts.length === 0) ? (
            <AppStack style={{ alignItems: 'center', padding: 40 }}>
              <AppText
                style={{
                  color: colorScheme.textColor,
                  fontSize: 18,
                  fontWeight: '500'
                }}
              >
                No products found
              </AppText>
            </AppStack>
          ) : (isTabletOrSmaller && config.mobileCarousel === 'true') ||
            (!isTabletOrSmaller && config.desktopCarousel === 'true') ? (
            <Carousel
              align={isTabletOrSmaller ? 'center' : 'start'}
              controlSize={40}
              draggable
              loop={false}
              slideGap={16}
              slideSize={`${
                (isTabletOrSmaller ? 85 : 100) /
                (isTabletOrSmaller
                  ? config.mobileColumnCount * 1 ?? 1
                  : config.desktopColumnCount * 1 ?? 6)
              }%`}
              slidesToScroll={
                isTabletOrSmaller
                  ? config.mobileColumnCount * 1 ?? 1
                  : config.desktopColumnCount * 1 ?? 6
              }
              withControls={!isTabletOrSmaller}
              withIndicators={false}
            >
              {loading
                ? Array.from(Array(config.desktopColumnCount * 1 ?? 6))
                    .slice(0, maxDisplayed)
                    .map((x, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <Carousel.Slide key={i}>
                        <WsProductCard
                          baseStoreUrl={baseProductsResourceUrl}
                          colorScheme={colorScheme}
                          hideAllProductBadges={hideAllProductBadges}
                          hideFreeShippingBadge={hideFreeShippingBadge}
                          isQuickAddEnabled={isQuickAddEnabled}
                          onNavigate={onNavigate}
                          portraitRatio={!squareRatio}
                          showSecondImageOnHover={showSecondImageOnHover}
                          showVendorName={showVendorName}
                        />
                      </Carousel.Slide>
                    ))
                : vendorProducts.slice(0, maxDisplayed).map((p) => (
                    <Carousel.Slide key={p.pkEcomStoreProduct}>
                      <WsProductCard
                        baseStoreUrl={baseProductsResourceUrl}
                        colorScheme={colorScheme}
                        ecomStoreProduct={p}
                        hideAllProductBadges={hideAllProductBadges}
                        hideFreeShippingBadge={hideFreeShippingBadge}
                        isQuickAddEnabled={isQuickAddEnabled}
                        onNavigate={onNavigate}
                        portraitRatio={!squareRatio}
                        showSecondImageOnHover={showSecondImageOnHover}
                        showVendorName={showVendorName}
                      />
                    </Carousel.Slide>
                  ))}
            </Carousel>
          ) : (
            <AppStack style={{}}>
              <Grid
                columns={
                  isTabletOrSmaller
                    ? config.mobileColumnCount * 1 ?? 1
                    : config.desktopColumnCount * 1 ?? 6
                }
              >
                {loading
                  ? Array.from(Array(config.desktopColumnCount * 1 ?? 6))
                      .slice(0, maxDisplayed)
                      .map((x, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Grid.Col key={i} span={1}>
                          <WsProductCard
                            baseStoreUrl={baseProductsResourceUrl}
                            colorScheme={colorScheme}
                            hideAllProductBadges={hideAllProductBadges}
                            hideFreeShippingBadge={hideFreeShippingBadge}
                            isQuickAddEnabled={isQuickAddEnabled}
                            onNavigate={onNavigate}
                            portraitRatio={!squareRatio}
                            showSecondImageOnHover={showSecondImageOnHover}
                            showVendorName={showVendorName}
                          />
                        </Grid.Col>
                      ))
                  : vendorProducts.slice(0, maxDisplayed).map((p) => (
                      <Grid.Col key={p.pkEcomStoreProduct} span={1}>
                        <WsProductCard
                          baseStoreUrl={baseProductsResourceUrl}
                          colorScheme={colorScheme}
                          ecomStoreProduct={p}
                          hideAllProductBadges={hideAllProductBadges}
                          hideFreeShippingBadge={hideFreeShippingBadge}
                          isQuickAddEnabled={isQuickAddEnabled}
                          onNavigate={onNavigate}
                          portraitRatio={!squareRatio}
                          showSecondImageOnHover={showSecondImageOnHover}
                          showVendorName={showVendorName}
                        />
                      </Grid.Col>
                    ))}
              </Grid>
            </AppStack>
          )}

          {config.enableViewAll === 'true' &&
            (ecomStoreCollection
              ? ecomStoreCollection.ecomStoreCollectionProductCount
              : state.paginatedEcomStoreProductsPreview.totalCount) >
              maxDisplayed && (
              <WsSectionViewAllButtons
                buttonStyleType={config.viewAllButtonStyle}
                colorScheme={colorScheme}
                onNavigate={onNavigate}
                url={
                  ecomStoreCollection
                    ? `${baseStoreUrl}/collections/${sterilizeUrlHandle(
                        ecomStoreCollection.name
                      )}`
                    : `${baseStoreUrl}/products`
                }
              />
            )}
        </AppStack>
      </AppStack>
    </WsSectionContainer>
  );
};

WsFeaturedCollection.propTypes = {
  baseStoreUrl: PropTypes.string,
  colorSchemes: PropTypes.array,
  config: PropTypes.object,
  ecomStoreTheme: PropTypes.object,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  themeConfig: PropTypes.object
};

export default WsFeaturedCollection;
